import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_DURATION_MS = 15_000;

export type RecorderState = 'idle' | 'recording' | 'stopped';

export interface RecorderResult {
  blob: Blob;
  durationMs: number;
}

export interface UseRecorderReturn {
  state: RecorderState;
  elapsedMs: number;
  start: () => Promise<void>;
  stop: () => void;
  result: RecorderResult | null;
  error: string | null;
  clear: () => void;
}

function pickMimeType(): string {
  const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const mt of preferred) {
    if (MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return '';
}

export function useRecorder(): UseRecorderReturn {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [result, setResult] = useState<RecorderResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);

  // Stable refs for cleanup helpers — avoids react-hooks/immutability issues
  const cleanupRef = useRef({
    clearTimer() {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    },
    stopStream() {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    },
    finishRecording() {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
      this.clearTimer();
    },
  });

  useEffect(() => {
    const cleanup = cleanupRef.current;
    return () => {
      cleanup.stopStream();
      cleanup.clearTimer();
    };
  }, []);

  const start = useCallback(async () => {
    const helpers = cleanupRef.current;
    setError(null);
    setResult(null);
    chunksRef.current = [];

    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          sampleRate: 44100,
        },
      });
    } catch {
      setError('Microphone access denied');
      return;
    }

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(streamRef.current, {
      mimeType,
      audioBitsPerSecond: 128000,
    });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const durationMs = Date.now() - startTimeRef.current;
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setResult({ blob, durationMs });
      setState('stopped');
      helpers.stopStream();
      helpers.clearTimer();
    };

    recorder.onerror = () => {
      setError('Recording failed');
      setState('idle');
      helpers.stopStream();
      helpers.clearTimer();
    };

    startTimeRef.current = Date.now();
    recorder.start(250);
    setState('recording');

    setElapsedMs(0);
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= MAX_DURATION_MS) {
        helpers.finishRecording();
      }
    }, 100);
  }, []);

  const stop = useCallback(() => {
    cleanupRef.current.finishRecording();
  }, []);

  const clear = useCallback(() => {
    setResult(null);
    setElapsedMs(0);
    setState('idle');
    setError(null);
  }, []);

  return { state, elapsedMs, start, stop, result, error, clear };
}
